/**
 * Main assessment container and state.
 */
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import humps from 'lodash-humps';
import isArray from 'lodash/isArray';
import isInteger from 'lodash/isInteger';
import isNull from 'lodash/isNull';
import uniqBy from 'lodash/uniqBy';
import React, { Component, PropTypes } from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import IntroOrResult from './IntroOrResult';
import Question from './Question';
/* global smoothScroll */

class Assessment extends Component {

  constructor(props) {
    super(props);
    if (!this.props.initialState) {
      console.log('Setting default state');
      history.pushState('', document.title, window.location.pathname);
      this.state = {
        assessmentData: this.props.assessmentData,
        currentPosition: -1,
        emailOverlay: false,
        emailReplaceHigh: null,
        emailReplaceIntro: null,
        emailReplaceLow: null,
        emailReplaceMedium: null,
        emailReplaceOutro: null,
        emailSent: false,
        readingListItems: [],
        resultUserEmail: null,
      };
    } else {
      const restoreState = this.props.initialState;
      if (restoreState.answers && restoreState.answers.length === 0) {
        restoreState.answers = {};
      }
      this.state = restoreState;
    }
    this.clearAssessmentResults = this.clearAssessmentResults.bind(this);
    this.emailResults = this.emailResults.bind(this);
    this.getQuestions = this.getQuestions.bind(this);
    this.hashChangeHandler = this.hashChangeHandler.bind(this);
    this.hashSync = this.hashSync.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.nodeId = this.props.nodeId;
    this.previousStep = this.previousStep.bind(this);
    this.recordChoice = this.recordChoice.bind(this);
    this.setEmailValues = this.setEmailValues.bind(this);
    this.setReadingListEntities = this.setReadingListEntities.bind(this);
    this.setResult = this.setResult.bind(this);
    this.toggleEmailOverlay = this.toggleEmailOverlay.bind(this);
    this.updateSessionState = this.updateSessionState.bind(this);
  }

  componentDidMount() {
    if (!this.props.initialState) {
      this.getQuestions();
    }
    this.hashChangeHandler();
  }

  componentDidUpdate() {
    this.updateSessionState();
  }

  /**
   * Notify drupal of changes in state.
   */
  clearAssessmentResults() {
    sessionStorage.removeItem(`assessmentData-${this.nodeId}`);
    sessionStorage.removeItem(`assessmentTimestamp-${this.nodeId}`);
    window.location.reload(true);
  }

  /**
   * Email goal results.
   */
  emailResults() {
    const {
      emailReplaceHigh,
      emailReplaceIntro,
      emailReplaceLow,
      emailReplaceMedium,
      emailReplaceOutro,
      emailSubject,
      resultUserEmail,
    } = this.state;
    const postBody = JSON.stringify({
      email: resultUserEmail,
      emailReplaceHigh,
      emailReplaceIntro,
      emailReplaceLow,
      emailReplaceMedium,
      emailReplaceOutro,
      subject: emailSubject,
      template: '7a50f458-2777-45b1-9ac6-f3f3f5f2cc7e',
    });
    // Send email to the user
    fetch('/api/email/send.json?_format=json', {
      method: 'POST',
      body: postBody,
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
    })
    .then(response => response.json())
    .then((result) => {
      this.setState({
        emailSent: true,
      });
      console.log(result);
    });
  }

  /**
   * Questions are paragraph entities and are not rendered in the original
   * node, so grab the array of rendered entities from a different endpoint.
   */
  getQuestions() {
    const { fieldQuestions } = this.props.assessmentData[0];
    const entityIds =
      fieldQuestions.map(question => parseInt(question.targetId, 10)).join('+');
    fetch(`/api/assessments/question/${entityIds}?_format=json`, { credentials: 'same-origin' })
    .then(response => response.json())
    .then((questions) => {
      this.setState({
        questions: humps(questions),
        answers: {},
      });
    })
    .catch((ex) => {
      console.log('parsing failed', ex);
    });
  }

  hashSync() {
    const { currentPosition } = this.state;
    const currentHash = window.location.hash;
    // If there isn't a hash, we should be on the intro screen
    if (currentHash === '' && currentPosition !== -1) {
      this.setState({
        currentPosition: -1,
      });
    } else {
      const hashPath = currentHash.split('/');
      // If the path is for a question, make sure that the index and the
      // hash are synced.
      if (hashPath[1] === 'question') {
        const hashIndex = parseInt(hashPath[2], 10);
        if (isInteger(hashIndex) && hashIndex !== currentPosition) {
          this.setState({
            currentPosition: hashIndex,
          });
        }
      // If the hash reflects results, make sure the index is equal to the
      // length of the questions array.
      } else if (hashPath[1] === 'results') {
        if (currentPosition !== this.state.questions.length) {
          this.setState({
            currentPosition: this.state.questions.length,
          });
        }
      }
    }
  }

  /**
   * Check to ensure the hash value and the currentPosition are synced.
   */
  hashChangeHandler() {
    // history.pushState('', document.title, window.location.pathname);
    this.hashSync();
    window.addEventListener('hashchange', this.hashSync);
  }

  /**
   * Increment the current position in the assessment.
   * -1 is the start, otherwise the step counter is the index in the questions
   * array.
   */
  nextStep() {
    const { currentPosition } = this.state;
    const newIndex = currentPosition + 1;
    this.updateHash(newIndex);
    this.setState({
      currentPosition: newIndex,
    });
    const bodyPadding = parseInt(window.getComputedStyle(
      document.querySelector('body'), null,
    ).getPropertyValue('padding-top'), 10);
    const scrollOptions = {
      offset: bodyPadding + 10,
    };
    smoothScroll.animateScroll(
      document.getElementById('assessment-app'),
      null,
      scrollOptions,
    );
  }

  /**
   * Decrement the current position in the assessment.
   */
  previousStep() {
    const { currentPosition } = this.state;
    const newIndex = currentPosition - 1;
    this.updateHash(newIndex);
    this.setState({
      currentPosition: newIndex,
    });
    const bodyPadding = parseInt(window.getComputedStyle(
      document.querySelector('body'), null,
    ).getPropertyValue('padding-top'), 10);
    const scrollOptions = {
      offset: bodyPadding + 10,
    };
    smoothScroll.animateScroll(
      document.getElementById('assessment-app'),
      null,
      scrollOptions,
    );
  }

  /**
   * Track choices by entity id and position index.
   */
  recordChoice(entityId, choice) {
    const { answers } = this.state;
    const newAnswers = answers;
    newAnswers[entityId] = choice;
    this.setState({
      answers: newAnswers,
    });
  }

  /**
   * Process questions for use by sendgrid email template.
   * @TODO: combine this with the filtering functionality that is in the results
   * component. Basically doing the same thing twice.
   */
  setEmailValues() {
    const {
      answers,
      assessmentData,
      questions,
      readingListItems,
    } = this.state;
    let emailReplaceHigh = '';
    let emailReplaceIntro = '';
    let emailReplaceLow = '';
    let emailReplaceMedium = '';
    let emailReplaceOutro = '';
    let emailSubject = 'Assessment results';
    // Loop through questions to compile medium and high result text.
    forEach(answers, (choice, entityId) => {
      const entity = find(questions, { id: entityId.toString() });
      const {
        fieldHighResult,
        fieldMediumResult,
      } = entity;
      if (choice <= 2) {
        emailReplaceMedium += fieldMediumResult;
      } else {
        emailReplaceHigh += fieldHighResult;
      }
    });
    if (assessmentData[0]) {
      const { fieldEmailSubject } = assessmentData[0];
      if (fieldEmailSubject[0]) {
        emailSubject = fieldEmailSubject[0] && fieldEmailSubject[0].value;
      }
    }
    // Map reading list items based on filtered list that is retrieved via
    // ajax after entities are known.
    emailReplaceLow = readingListItems.map((listItem, index) => {
      let renderedItem = `<li>${listItem.fieldArticle}</li>`;
      if (!index) {
        renderedItem = `<ul>${renderedItem}`;
      }
      if (index === (readingListItems.length - 1)) {
        renderedItem = `${renderedItem}</ul>`;
      }
      return renderedItem;
    }).join('');
    // If there are no high results, populate no strengths field
    if (emailReplaceHigh === '') {
      const { fieldNoStrengthsText, fieldNoStrengthsTitle } = assessmentData[0];
      const title = `<h4>${fieldNoStrengthsTitle[0] && fieldNoStrengthsTitle[0].value}</h4>`;
      const text = fieldNoStrengthsText[0] && fieldNoStrengthsText[0].value;
      emailReplaceIntro = `${title}${text}`;
    }
    // If there are only high results, populate the all strengths field
    if (emailReplaceLow === '' && emailReplaceMedium === '') {
      const { fieldAllStrengthsText, fieldAllStrengthsTitle } = assessmentData[0];
      const title = `<h4>${fieldAllStrengthsTitle[0] && fieldAllStrengthsTitle[0].value}</h4>`;
      const text = fieldAllStrengthsText[0] && fieldAllStrengthsText[0].value;
      emailReplaceOutro = `${title}${text}`;
    }
    // Compile results with intro text and title
    if (emailReplaceHigh.length) {
      const { fieldGoodIntroText, fieldGoodResultsTitle } = assessmentData[0];
      const title = fieldGoodResultsTitle[0] && fieldGoodResultsTitle[0].value;
      const text = fieldGoodIntroText[0] && fieldGoodIntroText[0].value;
      emailReplaceHigh = `<h2>${title}</h2>${text}${emailReplaceHigh}`;
    }
    if (emailReplaceMedium.length) {
      const { fieldMediumIntroText, fieldMediumResultsTitle } = assessmentData[0];
      const title = fieldMediumResultsTitle[0] && fieldMediumResultsTitle[0].value;
      const text = fieldMediumIntroText[0] && fieldMediumIntroText[0].value;
      emailReplaceMedium = `<h2>${title}</h2>${text}${emailReplaceMedium}`;
    }
    if (emailReplaceLow.length) {
      const { fieldLowIntroText, fieldLowResultsTitle } = assessmentData[0];
      const title = fieldLowResultsTitle[0] && fieldLowResultsTitle[0].value;
      const text = fieldLowIntroText[0] && fieldLowIntroText[0].value;
      emailReplaceLow = `<h2>${title}</h2>${text}${emailReplaceLow}`;
    }
    this.setState({
      emailReplaceHigh,
      emailReplaceIntro,
      emailReplaceLow,
      emailReplaceMedium,
      emailReplaceOutro,
      emailSubject,
    });
  }

  /**
   * Filter entities that will be included in the reading list.
   */
  setReadingListEntities() {
    const { answers, questions } = this.state;
    const readingListEntities = [];
    forEach(answers, (choice, entityId) => {
      if (!isNull(choice)) {
        const entity = find(questions, { id: entityId.toString() });
        if (entity) {
          const { id1 } = entity;
          if (choice === 0) {
            // Low results deviated from the model of 1 question to 1 result, so we
            // have to compile a list
            readingListEntities.push(id1);
          }
        }
      }
    });
    this.retrieveReadingList(readingListEntities);
  }

  /**
   * Get the compiled reading list for entities that are marked as low.
   * This is necessary to eliminate duplicates since they were puttin the same
   * articles in for multiple questions.
   */
  retrieveReadingList(readingListEntities) {
    if (readingListEntities.length) {
      const entityIds = readingListEntities.join('+');
      fetch(`/api/assessments/compiled/${entityIds}?_format=json`, { credentials: 'same-origin' })
      .then(response => response.json())
      .then((readingListItems) => {
        const filteredList = humps(uniqBy(readingListItems, 'field_article'));
        this.setState({
          readingListItems: filteredList,
        });
        this.setEmailValues();
      })
      .catch((ex) => {
        console.log('parsing failed', ex);
      });
    }
  }

  setResult(result) {
    this.setState({ ...result });
  }

  /**
   * Toggle display for email overlay on the results screen.
   */
  toggleEmailOverlay() {
    const { emailOverlay } = this.state;
    this.setState({
      emailOverlay: !emailOverlay,
    });
  }

  /**
   *
   */
  updateHash(newIndex) {
    const { questions } = this.state;
    if (newIndex === -1) {
      history.pushState('', document.title, window.location.pathname);
    } else if (isArray(questions) && (newIndex < questions.length)) {
      window.location.hash = `/question/${newIndex}`;
    } else if (isArray(questions) && (newIndex === questions.length)) {
      this.setReadingListEntities();
      this.setEmailValues();
      window.location.hash = '/results';
    }
  }

  /**
   * Notify drupal of changes in state.
   */
  updateSessionState() {
    const body = JSON.stringify(this.state);
    sessionStorage[`assessmentData-${this.nodeId}`] = body;
    sessionStorage[`assessmentTimestamp-${this.nodeId}`] =
      Math.floor(Date.now() / 1000);
  }

  render() {
    const { assessmentData, currentPosition } = this.state;
    const startAssessment = (currentPosition === -1);
    let currentAnswer;
    let currentQuestion;
    let endAssessment;
    // If we're not at the beginning, grab current question and check for end.
    if (!startAssessment) {
      currentQuestion = this.state.questions[currentPosition];
      endAssessment = (currentPosition === this.state.questions.length);
    }
    // Mark as in proress unless we're at the beginning or end.
    const inProgress = (!startAssessment && !endAssessment);
    // If we're on a question, see if it's already been answered. Used to
    // prepopulate values on next and previous.
    if (currentQuestion) {
      currentAnswer = this.state.answers[currentQuestion.id];
    }
    return (
      <div>
        {inProgress ?
          <ReactCSSTransitionGroup
            className={'animation-group'}
            transitionAppear
            transitionAppearTimeout={500}
            transitionEnterTimeout={500}
            transitionLeave={false}
            transitionName={'question'}
          >
            <Question
              currentAnswer={currentAnswer}
              currentPosition={currentPosition}
              currentQuestion={currentQuestion}
              key={`question-${currentQuestion.id}`}
              nextStep={this.nextStep}
              previousStep={this.previousStep}
              recordChoice={this.recordChoice}
              totalQuestions={this.state.questions.length}
            />
          </ReactCSSTransitionGroup>
        :
          <IntroOrResult
            answers={this.state.answers}
            assessmentData={assessmentData}
            clearAssessmentResults={this.clearAssessmentResults}
            currentPosition={currentPosition}
            emailOverlay={this.state.emailOverlay}
            emailResults={this.emailResults}
            emailSent={this.state.emailSent}
            nextStep={this.nextStep}
            previousStep={this.previousStep}
            questions={this.state.questions}
            readingListItems={this.state.readingListItems}
            resultUserEmail={this.state.resultUserEmail}
            setEmailValue={this.setEmailValue}
            setResult={this.setResult}
            toggleEmailOverlay={this.toggleEmailOverlay}
          />
        }
      </div>
    );
  }
}

Assessment.propTypes = {
  assessmentData: PropTypes.arrayOf(PropTypes.shape({
    fieldQuestions: PropTypes.arrayOf(PropTypes.shape({})),
  })),
  initialState: PropTypes.shape({}),
  nodeId: PropTypes.number,
};

export default Assessment;



// WEBPACK FOOTER //
// js/src/assessment/Assessment.js